import { gql, useQuery } from "@apollo/client";
import React from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { gettext } from "utils/text";

import { GroupPageQuery } from "~/components/Admin/Group/GroupPage.generated";
import { Breadcrumbs } from "~/components/common/breadcrumbs";
import { PrimaryHeading } from "~/components/common/text";
import { SettingsPageViewerFragment } from "~/components/Settings/SettingsPage.generated";

import GroupUsers from "./GroupUsers";

const GROUP_PAGE_QUERY = gql`
  query GroupPage($orgSlug: String!, $groupId: ID!) {
    orgGroup(orgSlug: $orgSlug, groupId: $groupId) {
      id
      name
      members {
        id
        firstName
        lastName
        email
        lastLogin
      }
    }
  }
`;

interface GroupPageProps {
  viewer: SettingsPageViewerFragment;
  orgSlug: string;
}

const GroupPage = ({ viewer, orgSlug }: GroupPageProps) => {
  const { groupId } = useParams<{ groupId: string }>();

  const { data, loading, error } = useQuery<GroupPageQuery>(GROUP_PAGE_QUERY, {
    variables: { orgSlug, groupId },
  });

  // Get org name from viewer
  const org = viewer.organizations?.filter(
    (org) => org.slug === orgSlug
  )[0] ?? { name: "" };

  if (error) {
    return (
      <Container>
        <div>Error loading group: {error.message}</div>
      </Container>
    );
  }

  const group = data?.orgGroup;

  return (
    <Container>
      <PrimaryHeading>
        <Breadcrumbs>
          <Link to="/groups">
          {org.name} {gettext("Groups")}
        </Link>
          <span>{group?.name || gettext("Loading...")}</span>
        </Breadcrumbs>
      </PrimaryHeading>
      <Content>
        <GroupUsers members={group?.members || []} loading={loading} />
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Content = styled.div`
  margin-top: 1rem;
`;

export default GroupPage;
